/**
 * ActivityList — W1.8
 *
 * Liste-Rendering mit Skeleton-Rows waehrend Fetch.
 * Load-More Button am Ende der Liste.
 */
import { useTranslation } from 'react-i18next'
import { ActivityListItem } from './ActivityListItem'
import { ActivityEmptyState } from './ActivityEmptyState'
import type { ActivityGroup } from './api/activityApi'

// --- Skeleton ---

function SkeletonRow() {
  return (
    <div className="list-row activity-item skeleton" aria-hidden="true">
      <div className="activity-icon-wrap skeleton-circle" />
      <div className="activity-content">
        <div className="skeleton-line" style={{ width: '60%', height: 14 }} />
        <div className="skeleton-line" style={{ width: '30%', height: 12, marginTop: 4 }} />
      </div>
      <div className="skeleton-line" style={{ width: 80, height: 12 }} />
    </div>
  )
}

function LoadingSkeleton() {
  const { t } = useTranslation('activity')
  return (
    <div aria-busy="true" aria-label={t('list.ariaLoading')}>
      {Array.from({ length: 5 }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  )
}

// --- Haupt-Komponente ---

type ActivityListProps = {
  items: ActivityGroup[]
  loading: boolean
  hasMore: boolean
  activeFilter: string | null
  /** Backend-Zeitfenster in Tagen — im Empty-State als Hinweis gerendert. */
  dayLimit?: number | null
  /** R5/FD: Nutzer-Einstellung Metric/Imperial fuer die Distanz-Detailzeile. */
  useMetric?: boolean
  onLoadMore: () => void
}

export function ActivityList({ items, loading, hasMore, activeFilter, dayLimit = null, useMetric = true, onLoadMore }: ActivityListProps) {
  const { t } = useTranslation('activity')
  const isEmpty = !loading && items.length === 0

  return (
    <section aria-label={t('list.ariaSection')}>
      {loading && items.length === 0 && <LoadingSkeleton />}

      {!loading && isEmpty && <ActivityEmptyState activeFilter={activeFilter} dayLimit={dayLimit} />}

      {items.length > 0 && (
        <div className="list activity-list" role="list" aria-label={t('list.ariaList')}>
          {items.map((item, index) => (
            <ActivityListItem key={item.group_key} item={item} index={index} useMetric={useMetric} />
          ))}
        </div>
      )}

      {loading && items.length > 0 && (
        <div aria-busy="true" aria-label={t('list.ariaLoadingMore')}>
          <SkeletonRow />
        </div>
      )}

      {!loading && hasMore && (
        <div className="load-more-wrap">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onLoadMore}
            aria-label={t('list.ariaLoadMore')}
          >
            {t('list.loadMore')}
          </button>
        </div>
      )}
    </section>
  )
}
